import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Package,
  CheckCircle2,
  Circle,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Calendar,
  DollarSign,
  Weight,
  ShoppingCart,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { ProcurementGroupingControls } from '../components/ui/procurement-grouping-controls';
import { GroupedProcurementItems } from '../components/ui/grouped-procurement-items';
import { formatCurrency, formatDate } from '../lib/utils';
import { ProcurementBatch, ProcurementBatchItem } from '../types';
import { 
  fetchProcurementBatchByToken, 
  toggleItemChecked,
  updateItemActualQuantity
} from '../services/procurementService';
import { db } from '../services/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  GroupingType,
  GroupingOptions,
  applyCombinedGroupingAndFiltering,
  getAvailableCategories,
  getAvailableBrands,
  getFlattenedItemsFromGroups
} from '../utils/procurementGroupingUtils';

// Debounce utility function
const useDebounce = (func: Function, delay: number) => {
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const debouncedFunction = useCallback((...args: any[]) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    const newTimer = setTimeout(() => {
      func(...args);
    }, delay);
    setDebounceTimer(newTimer);
  }, [func, delay, debounceTimer]);

  return debouncedFunction;
};

export function PublicProcurementBatch() {
  const { batchId, token } = useParams<{ batchId: string; token: string }>();
  const [batch, setBatch] = useState<ProcurementBatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingItems, setCheckingItems] = useState<Set<string>>(new Set());
  const [updatingQuantity, setUpdatingQuantity] = useState<Set<string>>(new Set());
  
  // Grouping and filtering state
  const [groupingOptions, setGroupingOptions] = useState<GroupingOptions>({
    groupBy: 'none',
    hideChecked: false,
    showOnlyIncomplete: false,
    selectedCategories: [],
    selectedBrands: []
  });

  // Load batch data on mount
  useEffect(() => {
    const loadBatch = async () => {
      if (!batchId || !token) {
        setError('Invalid procurement batch link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const batchData = await fetchProcurementBatchByToken(batchId, token);
        
        if (!batchData) {
          setError('Procurement batch not found or access denied');
          return;
        }
        
        setBatch(batchData);
      } catch (error) {
        console.error('Failed to load procurement batch:', error);
        setError('Failed to load procurement batch. Please check your link.');
      } finally {
        setLoading(false);
      }
    };

    loadBatch();
  }, [batchId, token]);

  // Set up real-time listener for batch updates
  useEffect(() => {
    if (!batchId || !batch) return;

    const batchRef = doc(db, 'procurementBatches', batchId);
    const unsubscribe = onSnapshot(batchRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setBatch(prev => prev ? {
          ...prev,
          checkedItems: data.checkedItems || [],
          actualQuantities: data.actualQuantities || {},
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } : null);
      }
    }, (error) => {
      console.error('Error listening to batch updates:', error);
    });

    return () => unsubscribe();
  }, [batchId, batch?.id]);

  const handleToggleItem = async (productId: string) => {
    if (!batch || !batchId || checkingItems.has(productId)) return;

    const isCurrentlyChecked = batch.checkedItems.includes(productId);
    const newCheckedState = !isCurrentlyChecked;
    const item = batch.items.find(i => i.productId === productId);

    try {
      setCheckingItems(prev => new Set(prev).add(productId));
      
      if (newCheckedState && item) {
        // When checking: update both checkbox and set actual quantity to required quantity
        await Promise.all([
          toggleItemChecked(batchId, productId, true),
          updateItemActualQuantity(batchId, productId, item.requiredQuantity)
        ]);
        
        toast.success(
          'Item marked as collected and quantity set to required amount',
          { duration: 3000 }
        );
      } else {
        // When unchecking: update checkbox and reset actual quantity to 0
        await Promise.all([
          toggleItemChecked(batchId, productId, false),
          updateItemActualQuantity(batchId, productId, 0)
        ]);
        
        toast.success(
          'Item unmarked and quantity reset to 0',
          { duration: 3000 }
        );
      }
    } catch (error) {
      console.error('Failed to toggle item:', error);
      toast.error('Failed to update item status');
    } finally {
      setCheckingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  // Function for updating actual quantities
  const updateActualQuantity = async (productId: string, actualQuantity: number) => {
    if (!batch || !batchId || updatingQuantity.has(productId)) return;

    const item = batch.items.find(i => i.productId === productId);
    if (!item) return;

    try {
      setUpdatingQuantity(prev => new Set(prev).add(productId));
      
      // Update actual quantity first
      await updateItemActualQuantity(batchId, productId, actualQuantity);
      
      // Then auto-update checkbox based on completion status
      const shouldBeChecked = actualQuantity >= item.requiredQuantity && actualQuantity > 0;
      const isCurrentlyChecked = batch.checkedItems.includes(productId);
      
      if (shouldBeChecked !== isCurrentlyChecked) {
        await toggleItemChecked(batchId, productId, shouldBeChecked);
        
        // Show appropriate notification for automatic action
        if (shouldBeChecked) {
          toast.success(
            'Item automatically checked (quantity complete)',
            { duration: 3000 }
          );
        } else if (actualQuantity === 0) {
          toast.success(
            'Item automatically unchecked (quantity set to 0)',
            { duration: 3000 }
          );
        } else {
          toast.success(
            'Item automatically unchecked (quantity incomplete)',
            { duration: 3000 }
          );
        }
      }
    } catch (error) {
      console.error('Failed to update actual quantity:', error);
      toast.error('Failed to update quantity');
    } finally {
      setUpdatingQuantity(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  // Debounced function for updating actual quantities
  const debouncedUpdateQuantity = useDebounce(updateActualQuantity, 500);

  const formatWeight = (item: ProcurementBatchItem) => {
    if (!item.netWeight || !item.weightUnit) return null;
    return `${item.netWeight}${item.weightUnit}`;
  };

  // Calculate available categories and brands
  const availableCategories = useMemo(() => {
    return batch ? getAvailableCategories(batch.items) : [];
  }, [batch]);

  const availableBrands = useMemo(() => {
    return batch ? getAvailableBrands(batch.items) : [];
  }, [batch]);

  // Apply grouping and filtering
  const groupedResult = useMemo(() => {
    if (!batch) {
      return { groups: [], totalItems: 0, hasGroups: false };
    }

    return applyCombinedGroupingAndFiltering(
      batch.items,
      groupingOptions,
      batch.checkedItems,
      batch.actualQuantities
    );
  }, [batch, groupingOptions]);

  // Get flattened items for non-grouped display
  const filteredItems = useMemo(() => {
    if (!batch) return [];
    
    if (groupedResult.hasGroups) {
      return getFlattenedItemsFromGroups(groupedResult);
    }

    // For non-grouped display, apply filters manually
    let items = batch.items;

    // Apply category filter
    if (groupingOptions.selectedCategories.length > 0) {
      items = items.filter(item => {
        const category = item.category || 'Uncategorized';
        return groupingOptions.selectedCategories.includes(category);
      });
    }

    // Apply brand filter
    if (groupingOptions.selectedBrands.length > 0) {
      items = items.filter(item => {
        const brand = item.brand || 'Unknown Brand';
        return groupingOptions.selectedBrands.includes(brand);
      });
    }

    // Apply incomplete items filter
    if (groupingOptions.showOnlyIncomplete) {
      items = items.filter(item => {
        const actualQuantity = batch.actualQuantities[item.productId] || 0;
        return actualQuantity > 0 && actualQuantity < item.requiredQuantity;
      });
    }

    // Separate checked and unchecked items for sorting
    const checkedItems = items.filter(item => 
      batch.checkedItems.includes(item.productId)
    );
    const uncheckedItems = items.filter(item => 
      !batch.checkedItems.includes(item.productId)
    );

    // Filter out checked items if requested
    if (groupingOptions.hideChecked) {
      return uncheckedItems;
    }

    return [...uncheckedItems, ...checkedItems];
  }, [batch, groupingOptions, groupedResult]);

  // Helper function to determine if an item is incomplete
  const isItemIncomplete = (item: ProcurementBatchItem) => {
    if (!batch) return false;
    const actualQuantity = batch.actualQuantities[item.productId] || 0;
    return actualQuantity > 0 && actualQuantity < item.requiredQuantity;
  };

  // Handler for updating grouping options
  const handleGroupingOptionsChange = (newOptions: Partial<GroupingOptions>) => {
    setGroupingOptions(prev => ({ ...prev, ...newOptions }));
  };
  const checkedCount = batch?.checkedItems.length || 0;
  const totalCount = batch?.items.length || 0;
  
  // Calculate completion stats
  const incompleteCount = batch?.items.filter(item => {
    const actualQuantity = batch.actualQuantities[item.productId] || 0;
    return actualQuantity > 0 && actualQuantity < item.requiredQuantity;
  }).length || 0;
  
  const completeCount = batch?.items.filter(item => {
    const actualQuantity = batch.actualQuantities[item.productId] || 0;
    return actualQuantity >= item.requiredQuantity;
  }).length || 0;

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Beach Background */}
        <div 
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-0"
          style={{
            backgroundImage: 'url(/beach-background.gif)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        {/* White Mask Overlay */}
        <div className="fixed inset-0 bg-white/60 z-0" />
        
        {/* Loading Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="relative z-10 text-center"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Procurement Batch...</h2>
          <p className="text-gray-600">Please wait while we fetch the data</p>
        </motion.div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Beach Background */}
        <div 
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-0"
          style={{
            backgroundImage: 'url(/beach-background.gif)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        {/* White Mask Overlay */}
        <div className="fixed inset-0 bg-white/60 z-0" />
        
        {/* Error Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="relative z-10 text-center max-w-md mx-4"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Please check your link or contact the administrator for assistance.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Beach Background */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-0"
        style={{
          backgroundImage: 'url(/beach-background.gif)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* White Mask Overlay */}
      <div className="fixed inset-0 bg-white/60 z-0" />
      
      {/* Content Layer */}
      <div className="relative z-10 min-h-screen">
        <div className="container mx-auto px-4 py-8 pb-14 max-w-6xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 mb-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-12 w-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-xl flex items-center justify-center">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-sans">
                  Procurement Batch: {batch.batchNumber}
                </h1>
                <p className="text-gray-600">
                  JamPack'd Purchase Order
                </p>
              </div>
            </div>

            {/* Batch Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Created</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatDate(batch.createdAt)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShoppingCart className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Orders</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {batch.orderIds.length}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Complete</span>
                </div>
                <div className="text-lg font-semibold text-emerald-600">
                  {completeCount}/{totalCount}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Incomplete</span>
                </div>
                <div className="text-lg font-semibold text-yellow-600">
                  {incompleteCount}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 mb-6"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <Badge 
                variant="outline"
                className="text-emerald-700 border-emerald-300 bg-emerald-50"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {completeCount} complete
              </Badge>
              <Badge 
                variant="outline"
                className="text-yellow-700 border-yellow-300 bg-yellow-50"
              >
                <AlertCircle className="w-3 h-3 mr-1" />
                {incompleteCount} incomplete
              </Badge>
              <Badge 
                variant="outline"
                className="text-blue-700 border-blue-300 bg-blue-50"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {checkedCount} collected
              </Badge>
            </div>
          </motion.div>

          {/* Grouping Controls */}
          <ProcurementGroupingControls
            options={groupingOptions}
            availableCategories={availableCategories}
            availableBrands={availableBrands}
            onOptionsChange={handleGroupingOptionsChange}
          />

          {/* Items List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {groupedResult.hasGroups ? (
              <GroupedProcurementItems
                groups={groupedResult.groups}
                batch={batch!}
                checkingItems={checkingItems}
                updatingQuantity={updatingQuantity}
                onToggleItem={handleToggleItem}
                onUpdateQuantity={updateActualQuantity}
                formatWeight={formatWeight}
              />
            ) : (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Procurement Items
                  </h3>
                </div>

            <div className="divide-y divide-gray-200">
              <AnimatePresence>
                {filteredItems.map((item, index) => {
                  const isChecked = batch.checkedItems.includes(item.productId);
                  const isChecking = checkingItems.has(item.productId);
                  const isUpdatingQuantity = updatingQuantity.has(item.productId);
                  const isPerformingLinkedOperation = isChecking || isUpdatingQuantity; // Either operation affects both
                  const weight = formatWeight(item);
                  const actualQuantity = batch.actualQuantities[item.productId] || 0;
                  const isIncomplete = isItemIncomplete(item);

                  return (
                    <motion.div
                      key={item.productId}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`p-4 sm:p-5 rounded-xl border transition-all duration-300 backdrop-blur-sm ${
                        isIncomplete 
                          ? 'bg-gradient-to-r from-amber-50/80 to-yellow-50/80 border-amber-200 shadow-amber-100/50 hover:shadow-amber-200/30 hover:shadow-lg' 
                          : isChecked 
                            ? 'bg-gradient-to-r from-emerald-50/80 to-green-50/80 border-emerald-200 shadow-emerald-100/50 hover:shadow-emerald-200/30 hover:shadow-lg' 
                            : 'bg-white/60 border-gray-200/60 hover:bg-white/80 hover:border-gray-300/60 hover:shadow-lg'
                      } ${isPerformingLinkedOperation ? 'ring-2 ring-blue-300/30' : ''}`}
                    >
                      {/* Mobile-First Responsive Layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        {/* Top Row: Checkbox, Image, Product Info, Status */}
                        <div className="flex items-center gap-3 flex-1">
                          {/* Checkbox */}
                          <div className="flex-shrink-0 flex items-center gap-2">
                            <div className="relative">
                              <Checkbox
                                checked={isChecked}
                                disabled={isPerformingLinkedOperation}
                                onCheckedChange={() => handleToggleItem(item.productId)}
                                className="w-5 h-5 rounded-lg border-2 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500 transition-all duration-200 shadow-sm"
                              />
                              {isPerformingLinkedOperation && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Product Image */}
                          <div className="flex-shrink-0 relative">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden shadow-md ring-1 ring-black/5 bg-gradient-to-br from-gray-50 to-gray-100">
                              <img 
                                src={item.image} 
                                alt={item.productName}
                                className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
                                onError={(e) => {
                                  e.currentTarget.src = '/api/placeholder/64/64';
                                }}
                              />
                            </div>
                          </div>
                          
                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <div className={`font-semibold text-gray-900 text-sm sm:text-base leading-tight ${
                              isChecked ? 'line-through text-gray-500/80' : ''
                            }`}>
                              {item.productName}
                            </div>
                            {item.brand && (
                              <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                                {item.brand}
                              </div>
                            )}
                            {weight && (
                              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 mt-1 bg-gray-50/80 rounded-md px-2 py-0.5 w-fit">
                                <Weight className="w-3 h-3 text-gray-500" />
                                <span className="font-medium">{weight}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Status Indicator - Hidden on mobile, shown on desktop */}
                          <div className="hidden sm:flex flex-shrink-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                              isChecked 
                                ? 'bg-emerald-100 text-emerald-600 shadow-sm' 
                                : 'bg-gray-100 text-gray-400'
                            }`}>
                              {isChecked ? (
                                <CheckCircle2 className="w-5 h-5" />
                              ) : (
                                <Circle className="w-5 h-5" />
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Bottom Row: Quantity Info + Mobile Status */}
                        <div className="flex items-center justify-between sm:justify-end gap-3">
                          {/* Quantity Info */}
                          <div className="flex items-center gap-3 sm:gap-5">
                            {/* Required Quantity */}
                            <div className="text-right">
                              <div className="text-xs sm:text-sm font-semibold text-gray-700 bg-gray-100/80 rounded-lg px-2.5 py-1.5">
                                <span className="sm:hidden text-gray-500">Need: </span>
                                <span className="hidden sm:inline text-gray-600">{item.requiredQuantity} required</span>
                                <span className="sm:hidden text-gray-800">{item.requiredQuantity}</span>
                              </div>
                            </div>
                            
                            {/* Actual Quantity Input */}
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                                <span className="text-xs text-gray-500 font-medium sm:hidden">Got:</span>
                                <div className="relative">
                                  <input
                                    type="number"
                                    min="0"
                                    max={item.requiredQuantity * 2}
                                    value={actualQuantity}
                                    onChange={(e) => {
                                      const value = Math.max(0, parseInt(e.target.value) || 0);
                                      debouncedUpdateQuantity(item.productId, value);
                                    }}
                                    disabled={isPerformingLinkedOperation}
                                    placeholder="0"
                                    className="w-16 h-9 px-2 py-1 text-sm font-semibold border-2 border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 disabled:bg-gray-50 disabled:text-gray-400 transition-all duration-200 shadow-sm"
                                  />
                                  {isPerformingLinkedOperation && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                                      <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                                    </div>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 font-medium hidden sm:inline">actual</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Status Indicator - Shown on mobile only */}
                          <div className="flex sm:hidden flex-shrink-0">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                              isChecked 
                                ? 'bg-emerald-100 text-emerald-600 shadow-sm' 
                                : 'bg-gray-100 text-gray-400'
                            }`}>
                              {isChecked ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : (
                                <Circle className="w-4 h-4" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

                {filteredItems.length === 0 && groupingOptions.hideChecked && (
                  <div className="p-8 text-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      All Items Collected!
                    </h3>
                    <p className="text-gray-600">
                      Great job! All remaining items have been collected.
                    </p>
                  </div>
                )}

                {filteredItems.length === 0 && !groupingOptions.hideChecked && (
                  <div className="p-8 text-center">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Items Found
                    </h3>
                    <p className="text-gray-600">
                      No items match your current filter criteria.
                    </p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-6 text-center text-sm text-gray-500"
          >
            <p>JamPack'd Procurement System</p>
            <p>Last updated: {formatDate(batch.updatedAt)}</p>
          </motion.div>
        </div>
      </div>

      {/* Sticky Footer Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <button
          onClick={() => window.open('https://www.paulsmith.codes', '_blank', 'noopener,noreferrer')}
          className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white py-2 px-4 text-xs font-medium transition-all duration-200 hover:shadow-lg cursor-pointer border-none"
        >
          Developed by PaulSmith.Codes
        </button>
      </div>
    </div>
  );
}